<template>
  <AppLayout :title="news.title">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="md:flex md:items-center md:justify-between">
            <div class="flex-1 min-w-0">
              <nav class="flex mb-4" aria-label="Breadcrumb">
                <ol class="inline-flex items-center space-x-1 md:space-x-3">
                  <li class="inline-flex items-center">
                    <Link
                      :href="route('home')"
                      class="text-gray-700 hover:text-gray-900 inline-flex items-center"
                    >
                      {{ $t('Übersicht') }}
                    </Link>
                  </li>
                  <li>
                    <div class="flex items-center">
                      <span class="text-gray-400">/</span>
                      <Link
                        :href="route('news')"
                        class="ml-1 text-gray-700 hover:text-gray-900 md:ml-2"
                      >
                        {{ $t('Neuigkeiten') }}
                      </Link>
                    </div>
                  </li>
                  <li>
                    <div class="flex items-center">
                      <span class="text-gray-400">/</span>
                      <span class="ml-1 text-gray-500 md:ml-2">{{ news.title }}</span>
                    </div>
                  </li>
                </ol>
              </nav>
              <h1 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {{ news.title }}
              </h1>
              <div class="mt-2 flex items-center text-sm text-gray-500">
                <span>{{ formatDate(news.date) }}</span>
              </div>
            </div>
            <div class="mt-4 flex md:mt-0 md:ml-4">
              <Link
                :href="route('news')"
                class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
              >
                ← {{ $t('Zurück zu den Neuigkeiten') }}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article class="bg-white rounded-lg shadow">
          <!-- Featured Image -->
          <div v-if="news.featured_image_path" class="mb-6">
            <img
              :src="getImageUrl(news.featured_image_path)"
              :alt="news.title"
              class="w-full h-64 object-cover rounded-t-lg"
            />
          </div>

          <div class="p-6">
            <!-- Subtitle -->
            <div v-if="news.subtitle" class="mb-6">
              <p class="text-xl text-gray-600 leading-relaxed">
                {{ news.subtitle }}
              </p>
            </div>

            <!-- Content -->
            <div class="prose max-w-none prose-lg prose-sonnenglas">
              <div v-html="news.content"></div>
            </div>

            <!-- Meta Information -->
            <div class="mt-8 pt-6 border-t border-gray-200">
              <div class="flex items-center text-sm text-gray-500">
                <span>{{ $t('Veröffentlicht am') }} {{ formatDate(news.date) }}</span>
                <span v-if="news.updated_at !== news.created_at" class="ml-4">
                  {{ $t('Zuletzt aktualisiert') }} {{ formatDate(news.updated_at) }}
                </span>
              </div>
            </div>

            <!-- Navigation -->
            <div class="mt-8 pt-6 border-t border-gray-200">
              <div class="flex justify-between">
                <Link
                  :href="route('news')"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-sonnenglas-700 bg-sonnenglas-100 hover:bg-sonnenglas-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
                >
                  ← {{ $t('Alle Neuigkeiten') }}
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { inject } from 'vue'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const route = inject('route')

const props = defineProps({
  news: Object,
})

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const locale = document.documentElement.lang || 'de-DE'
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const getImageUrl = (path) => {
  // This would fetch the image from the API - for now return a placeholder
  return `/api/partner-portal/images/${path}`
}
</script>

<style>
/* Custom prose styles for SONNENGLAS content */
.prose-sonnenglas {
  @apply text-gray-700;
}

.prose-sonnenglas h1,
.prose-sonnenglas h2,
.prose-sonnenglas h3,
.prose-sonnenglas h4,
.prose-sonnenglas h5,
.prose-sonnenglas h6 {
  @apply text-gray-900;
}

.prose-sonnenglas a {
  @apply text-sonnenglas-600 hover:text-sonnenglas-700;
}

.prose-sonnenglas strong {
  @apply text-gray-900 font-semibold;
}
</style>